import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { environment } from '../../../../environments/environment';
import { DIALOG_DATA } from '../../../../global.variable';

import { Capacitor } from '@capacitor/core';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import { UserProfileService } from '../../../profile/user-profile/user-profile.service';
import { DeviceInfoService } from '../../services/device-info.service';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { DialogRef } from '../modal-dialogue/modal-dialogue.ref';

@Component({
  selector: 'gtapp-splash-screen',
  standalone: false,
  templateUrl: './splash-screen.component.html',
  styleUrl: './splash-screen.component.scss',
})
export class SplashScreenComponent implements OnInit, OnDestroy, AfterViewInit {
  gpsStatusInfo: number = 0;

  showLocationPermissionCurrentStep: number = 0;

  notificationAccessCheck: boolean = false;
  cameraPromptCheck: boolean = false;

  notificationStatusInfo: number = 0;

  locationSettingCheck = { global: false, app: false };

  isNativeApp = Capacitor.isNativePlatform();
  devicePlatform = '';

  constructor(
    protected dialogueRef: DialogRef,
    @Inject(DIALOG_DATA) public dialogData: any,
    private toasterService: ToasterService,
    private deviceInfoService: DeviceInfoService,
    private swPush: SwPush,
    private profileService: UserProfileService,
    private spinnerService: LoadingSpinnerService,
  ) {}
  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.cameraPromptCheck = this.dialogData?.cameraPromptCheck;
    if (this.isNativeApp) {
      this.devicePlatform = Capacitor.getPlatform();
    }
  }
  ngOnDestroy(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }
  handleVisibilityChange = () => {
    if (!document.hidden && this.isNativeApp) {
      window.location.reload();
    }
  };
  async ngAfterViewInit() {
    this.cameraPromptCheck = this.dialogData?.cameraPromptCheck;
    if (!this.cameraPromptCheck) {
      const isDevicePWA = this.deviceInfoService.isDevicePWA();
      const isFirstTime = this.dialogData?.firstTime;
      if (
        isDevicePWA &&
        this.swPush?.isEnabled &&
        isFirstTime &&
        !this.isNativeApp
      ) {
        let existingSub = await this.checkExisitingSubscription();
        if (!existingSub) {
          this.notificationAccessCheck = true;
          this.notificationStatusInfo = 1;
          this.requestSubscription();
        } else {
          this.notificationAccessCheck = false;
          this.checkGpsStatus();
        }
      } else {
        this.notificationAccessCheck = false;
        this.checkGpsStatus();
      }
    }
  }
  sleep(ms = 1000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  async checkGpsStatus() {
    // 1: Checking for permission
    // 2: Provide access if prompted
    // 3: Fetched GPS successfully
    // 4: Permission denied by browser/device
    // 5: Some other error
    // 6: Browser doesn't support GPS
    this.gpsStatusInfo = 1;
    await this.sleep(1000);
    this.gpsStatusInfo = 2;
    await this.sleep(2000);
    try {
      const gps = await this.deviceInfoService.getGpsForPagesComponent();

      this.gpsStatusInfo = 3;
    } catch (error: any) {
      if (error?.code !== error.PERMISSION_DENIED) {
        this.locationSettingCheck = {
          app: false,
          global: true,
        };

        this.gpsStatusInfo = 4;
      } else if (error?.message == 'user-denied-permission') {
        this.locationSettingCheck = {
          app: true,
          global: false,
        };
        this.gpsStatusInfo = 4;
      } else if (error?.message == 'permission-denied') {
        this.gpsStatusInfo = 4;
      } else {
        this.gpsStatusInfo = 5;
      }
    }
  }
  async openSettings() {
    this.spinnerService.show('Opening settings.');
    await this.sleep(500);
    this.spinnerService.hide();
    if (this.locationSettingCheck.app) {
      NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } else {
      NativeSettings.open({
        optionAndroid: AndroidSettings.Location,
        optionIOS: IOSSettings.LocationServices,
      });
    }
  }

  onDismiss(reload = false): void {
    localStorage.setItem(btoa('isIOSFirstTimeUser'), 'false');
    this.onCloseDialogue();
    if (reload) window.location.reload();
  }
  onCloseDialogue(val = false) {
    if (val && this.cameraPromptCheck) this.updateSubscriberUserSettings();
    this.dialogueRef.close(val);
  }
  async checkExisitingSubscription() {
    return new Promise((resolve) => {
      this.swPush?.subscription.subscribe((res: any) => {
        resolve(res);
      });
    });
  }
  async unsubscribeFromPush() {
    try {
      await this.swPush.unsubscribe();
      // Successfully unsubscribed
    } catch (err) {}
  }
  requestSubscription = async () => {
    await this.sleep(1000);
    this.notificationStatusInfo = 2;
    await this.sleep(1000);

    this.swPush
      .requestSubscription({
        serverPublicKey: environment.vapIDPKey,
      })
      .then((_) => {
        this.profileService
          .registerPushNotification({ subscription: JSON.stringify(_) })
          .then(async (response: any) => {
            if (response['status'] == 'success') {
              this.notificationStatusInfo = 3;
              await this.sleep(1000);
              this.notificationAccessCheck = false;
              this.checkGpsStatus();
            } else {
              this.notificationStatusInfo = 4;
              this.notificationAccessCheck = false;
              this.checkGpsStatus();
              this.toasterService.setMessage({
                successMessage: '',
                errorMessage: response['message'],
              });
            }
          });
      })
      .catch(async (_: any) => {
        let existingSub = await this.checkExisitingSubscription();
        if (existingSub) {
          await this.unsubscribeFromPush();
          await this.requestSubscription();
        } else {
          this.notificationStatusInfo = 4;
          this.notificationAccessCheck = false;
          this.checkGpsStatus();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: _,
          });
        }
      });
  };

  updateSubscriberUserSettings() {
    const userGuide = JSON.parse(localStorage.getItem('userGuide') || '{}');
    userGuide['hide_safari_access_prompts'] = true;
    localStorage.setItem('userGuide', JSON.stringify(userGuide));

    this.profileService
      .updateUser(
        {
          data: {
            user_guide_info: userGuide,
          },
        },
        { save_preference: 1 },
        false,
      )
      .then((response: any) => {
        if (response?.status === 'success') {
          localStorage.setItem('userGuide', JSON.stringify(userGuide));
        }
      });
  }
}
